// User types
export interface User {
  id: string;
  email: string;
  name: string | null;
  display_name?: string | null;
  phone: string | null;
  role: string;
  surfaces: Surface[];
  team_id?: string | null;
  is_active: boolean;
  google_picture_url: string | null;
  metadata: Record<string, any>;
  has_password?: boolean;
}

// core's role vocabulary (services/core/app/constants/user_roles.py).
export const USER_ROLES = ['admin', 'manager', 'sales', 'pre_sales', 'advisor', 'marketing'] as const;

// core's surface vocabulary (services/core/app/constants/surfaces.py): the
// portals a user may sign in to. An allowlist — unticked means refused.
export const SURFACES = [
  { id: 'nurture', label: 'Nurture' },
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'marketing', label: 'Marketing' },
] as const;
export type Surface = (typeof SURFACES)[number]['id'];

// A Sell.do team as core mirrors it (MAG-168).
export interface Team {
  id: string;
  name: string;
  is_active: boolean;
  head_user_ids: string[];
  allied_user_ids: string[];
}

// One pickable person in the scope control (GET /users/me/scope).
export interface ScopeUser {
  id: string;
  display_name: string | null;
  name: string | null;
  role: string;
  team_id: string | null;
  team_name: string | null;
  leads: number;
}

export interface ScopeResponse {
  all: boolean;
  users: ScopeUser[];
}

// WhatsApp Template Types
export {
  type WhatsAppTemplateType,
  type WhatsAppTemplateScope,
  type WhatsAppTemplateStatus,
  type WhatsAppTemplateCategory,
  type WhatsAppButtonType,
  type WhatsAppHeaderFormat,
  type WhatsAppButton,
  type CarouselCard,
  type WhatsAppTemplate,
  type SuggestedTemplate,
  type SuggestTemplatesResponse,
  TEMPLATE_VALIDATION_RULES,
  isCarouselTemplate,
  hasMediaHeader,
  canHaveFooter,
  canHaveButtons,
  getTemplateTypeLabel,
  getTemplateStatusColor,
} from './whatsapp';

// Lead types
export interface Lead {
  id: string;
  name: string;
  mobile: string | null;
  email: string | null;
  alternate_mobile: string | null;
  source: string;
  source_id: string | null;
  enriched_profile: EnrichedProfile | null;
  enrichment_status: string;
  enrichment_last_attempted_at: string | null;
  net_worth_estimate: number | null;
  location: string | null;
  is_active: boolean;
  tags: string[] | null;
  created_at: string;
  updated_at: string;
  // Followup pointer (Sell.do mirror; used by the CSV export)
  next_followup_date: string | null;
  next_followup_type: string | null;
  // The one ladder (lib.ts STAGES)
  stage: string;
  stage_reason?: string | null;          // write-only, on an exit
  stage_reason_tags?: string[] | null;   // write-only, on an exit
  stage_changed_at?: string | null;
  crm_stage_label?: string | null;
  // Discovery fields
  investor_type: string | null;
  needs_bank_loan: string | null;
  budget_range_min: number | null;
  budget_range_max: number | null;
  preferred_villa_type: string | null;
  family_size: number | null;
  primary_motivation: string | null;
  buyer_location: string | null;
  purchase_timeline: string | null;
  // Conversation link
  conversation_id: string | null;
  // First touch (immutable): the arrival facts stamped at creation and the
  // one-vocabulary label core derives from them (`meta · paid · <ad>`).
  acquisition?: Record<string, any> | null;
  acquisition_label?: string | null;
  // Do-not-contact axis + denormalized action pointers (current core contract)
  do_not_contact_at?: string | null;
  do_not_contact_reason?: string | null;
  next_action_at?: string | null;
  next_action_type?: string | null;
  last_activity_at?: string | null;
  last_activity_type?: string | null;
  selldo_lead_id?: string | null;
  // Advisor assignments
  assignments?: LeadAssignment[];
}

// Seed enrichment (public records) — core copies it from the matched seed.
export interface EnrichedVehicle {
  vehicle_number?: string | null;
  make?: string | null;
  model?: string | null;
  cc?: number | string | null;
}

export interface EnrichedProfile {
  address?: string | null;
  city?: string | null;
  pin_code?: string | null;
  state_code?: string | null;
  make?: string | null;
  model?: string | null;
  vehicle_cc?: number | string | null;
  vehicles?: EnrichedVehicle[] | null;
  enrichment?: {
    first_name?: string | null;
    last_name?: string | null;
    title?: string | null;
    gender?: string | null;
    entity_type?: string | null;
    industry?: string | null;
    caste?: string | null;
    community_religion?: string | null;
    community_language?: string | null;
    locality?: string | null;
    hni_indicator?: string | null;
    state_code?: string | null;
  } | null;
}

// Advisor assignment types
export type AdvisorStage = 'presales' | 'sitevisit' | 'closure';

export interface LeadAssignment {
  stage: AdvisorStage;
  user_id: string;
  user_name?: string;
  user_email?: string;
  assigned_at: string;
  assigned_by?: string;
}

export interface SourceMapping {
  id: string;
  source: string;
  user_id: string;
  user_name?: string;
  created_at: string;
}

export interface LeadListResponse {
  total: number;
  page: number;
  page_size: number;
  data: Lead[];
}

// ---------------------------------------------------------------------------
// Activities — the lead's immutable timeline (core GET /leads/{id}/activities).
// One `call` per dial, one `message` per WhatsApp window, `note`, `visit`;
// `payload` is per-kind (call: connection_result, duration_seconds,
// recordings[]; message: counts, messages[]; note: text, recordings[]).
// ---------------------------------------------------------------------------
export type ActivityKind = 'call' | 'message' | 'note' | 'visit' | 'stage_change' | 'assignment';
export type ClosedReason = 'completed' | 'expired' | 'cancelled' | 'rejected';

export interface Activity {
  id: string;
  lead_id: string;
  kind: ActivityKind;
  channel: 'phone' | 'whatsapp' | 'internal' | 'system';
  direction: 'inbound' | 'outbound' | 'none';
  occurred_at: string;
  actor_user_id: string | null;
  source: string;
  source_ref: string | null;
  conversation_ref: string | null;
  window_expires_at: string | null;
  task_id: string | null;
  summary: string | null;
  payload: Record<string, any>;
  created_at: string;
}

export interface LeadActivityResponse {
  activity: Activity[];
  total: number;
}

export interface LeadNote {
  id: string;
  lead_id: string;
  content: string;
  selldo_activity_id?: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Voice-note audio (same entry shape as a call's payload.recordings[]).
  // Empty for typed notes.
  recordings?: { url: string; transcription?: { status: string } }[];
}

// Tracked-link engagement (core GET /leads/{id}/engagement) — lifetime scope.
export interface LeadEngagementLink {
  link_id: string;
  // The visitor's one key: bfg_t cookie = Clarity User ID = GA4 user_id.
  token: string;
  form_type: string;
  created_by: string;
  opened_at: string | null;
  last_opened_at: string | null;
  open_count: number | null;
  button_clicked_at: string | null;
  button_intent: string | null;
  video_pct_watched: number | null;
  submitted_at: string | null;
  frame_views: number;
  distinct_frames: number;
  dwell_ms_total: number;
  likes: number;
  liked_frames: string[];
  shares: number;
  shared_frames: string[];
  brochure_opens: number;
  sources: string[];
  /** every source the link was opened from, labelled by core — never parse a tag */
  source_labels: string[];
  /** per album frame: album position (null once it left the album), views, dwell */
  frame_stats?: { frame: string; idx: number | null; views: number; dwell_ms: number }[];
}

export interface LeadEngagementTotals {
  links: number;
  opens: number;
  frame_views: number;
  distinct_frames: number;
  dwell_ms_total: number;
  likes: number;
  shares: number;
  brochure_opens: number;
  cta_clicked: boolean;
  max_video_pct: number;
  submitted: boolean;
  last_activity_at: string | null;
}

export interface LeadEngagement {
  lead_id: string;
  totals: LeadEngagementTotals;
  links: LeadEngagementLink[];
}

// Understanding box (free-form topic map; replaces knowledge-profile)
export interface UnderstandingTopic {
  value?: string | null;
  detail?: string | null;
  stance?: string | null;
  delivery?: string | null;
  /** evidence trail; the latest `at` sorts the box and its `channel` (call/chat) is shown */
  events?: { at?: string; channel?: string; [k: string]: any }[] | null;
}

export interface Understanding {
  lead_id: string;
  understanding: Record<string, UnderstandingTopic> | null;
}

// WhatsApp message type
export interface WhatsAppMessage {
  direction: 'inbound' | 'outbound';
  text: string;
  sent_at?: string;
  received_at?: string;
  template_name?: string;
  status?: string;
}

// ---------------------------------------------------------------------------
// Task model (current core contract — the live work queue).
// GET /api/v1/leads/{id}/tasks; verbs go through nurture /actions/{id}/*.
// ---------------------------------------------------------------------------
export type TaskType = 'call' | 'whatsapp' | 'site_visit' | 'other';
// `proposed` = AI-suggested from a call summary; invisible to the live queue
// until an operator approves (→ open) or rejects (→ cancelled/'rejected').
export type TaskStatus = 'open' | 'done' | 'cancelled' | 'proposed';

export interface Task {
  id: string;
  lead_id: string;
  type: TaskType;
  status: TaskStatus;
  due_at: string;
  expires_at: string | null;
  assignee_user_id: string | null;
  result_activity_id: string | null;
  /** a dial awaiting its transcript verdict — the task is "checking the call",
   *  not overdue, for the minutes until the stamp resolves it */
  pending_result_activity_id: string | null;
  closed_reason: ClosedReason | null;
  defer_count: number;
  scheduler_handle: string | null;
  source: string;
  source_ref: string | null;
  campaign_id: string | null;
  /** links a spawned task (a visit's reconfirm call) to its parent visit */
  parent_task_id: string | null;
  payload: Record<string, any>;
  created_at: string;
  updated_at: string;
  lead?: Lead | null;
}

// POST /api/v1/leads/{id}/suggest — created → `task` is the proposed task;
// otherwise `skip_reason` says why (no_action_warranted carries the model's
// reason in suggestion.reason).
export interface SuggestResponse {
  created: boolean;
  task?: Task | null;
  suggestion?: { type?: string; reason?: string; due_at?: string; template?: string } | null;
  skip_reason?: string | null;
}

export interface TaskListResponse {
  total: number;
  tasks: Task[];
}

/** A cross-lead activity row (GET /api/v1/activities): core embeds the lead snapshot (joinedload). */
export type LeadActivity = Activity & { lead?: Lead | null };

// Site-visit booking — POST /api/v1/leads/{id}/site-visit (the single path;
// re-posting with a new date/slot reschedules the open visit).
// `time_slot` is a slot name, or a clock time "HH:MM" (24h IST) for a custom
// time — core labels that one "2:30 PM".
export type SiteVisitNamedSlot = 'early_morning' | 'morning' | 'afternoon' | 'evening';
export type SiteVisitTimeSlot = SiteVisitNamedSlot | string;
export type SiteVisitPickupOption = 'home_pickup' | 'from_office' | 'own_vehicle' | 'tbd';

export interface SiteVisitBookRequest {
  visit_date: string; // YYYY-MM-DD
  time_slot: SiteVisitTimeSlot;
  pickup_option: SiteVisitPickupOption;
  location?: string; // only meaningful for home_pickup
}

export interface SiteVisitBookResponse {
  action_id: string; // the site_visit Task id (frozen consumer contract key)
  scheduled_at: string; // IST isoformat
  title: string;
}

// Lead list filters (mirror core's GET /api/v1/leads query params)
export interface LeadFilters {
  search?: string;
  source?: string;
  location?: string;
  is_active?: boolean;
  stage?: string;
  tag?: string;        // exact "dimension:value" the lead must carry
  has_tags?: boolean;  // true = only tagged, false = only untagged
}

// GET /api/v1/leads/tags/summary — live tag distribution for the dashboard
export interface TagValueCount {
  value: string;
  count: number;
  lead_id: string | null; // set only when count === 1 (direct profile link)
}

export interface TagDimension {
  dimension: string;
  total: number;
  values: TagValueCount[];
}

export interface TagsSummary {
  total_active: number;
  tagged: number;
  untagged: number;
  dimensions: TagDimension[];
}


// GET /api/v1/scheduling/windows — the call-cadence policy's daily calling
// windows, in the policy's own vocabulary. `start`/`end` are "HH:MM" IST.
export interface CallWindow {
  name: string;
  start: string;
  end: string;
}

export interface CallWindowsResponse {
  timezone: string;
  windows: CallWindow[];
}

// ── /users/me/* — the operator's own reads (core schemas/dashboard.py) ──

export interface MyLeadsSummary {
  total: number;
  /** stage → count over the caller's contactable book; empty stages absent */
  by_stage: Record<string, number>;
}

export interface TodayStats {
  dials: number;
  connected: number;
  talk_seconds: number;
  leads_reached: number;
  /** connected AND long enough to be a real conversation */
  conversations: number;
}

/** One dot on the follow-up radar: an open task placed by due_at. */
export interface FollowupDot {
  task_id: string;
  lead_id: string;
  lead_name: string;
  stage: string | null;
  due_at: string;
  type: string;
  tags: string[];
}

/** One dot on the activity trail: something the operator did. */
export interface ActivityDot {
  activity_id: string;
  lead_id: string;
  lead_name: string;
  stage: string | null;
  kind: string;
  /** calls only: connected | no_answer | missed | voicemail | auto_responder */
  outcome: string | null;
  occurred_at: string;
}
